import { Empty } from 'google-protobuf/google/protobuf/empty_pb'
import { useSwrImmutableGrpc } from 'src/components/async/useSwrImmutableGrpc'
import { PromptOverrideSourcesPromiseClient } from '@trustero/trustero-api-web/lib/promptoverridesources/promptoverridesources_grpc_web_pb'
import { AccountSettingsPromiseClient } from '@trustero/trustero-api-web/lib/accountsettings/accountsettings_grpc_web_pb'
import { GrpcResponse, NewGrpcResponse } from 'src/components/async/hooks/types'
import {
  CreatePromptOverrideSourceRequest,
  DeletePromptOverrideSourceRequest,
  GetPromptOverrideSourceDownloadUrlRequest,
  ListPromptOverrideSourcesResponse,
} from '@trustero/trustero-api-web/lib/promptoverridesources/promptoverridesources_pb'
import { useAuthorizedGrpcClient } from 'src/adapter/grpcClient'
import { useConfirmationContext } from 'src/Confirmation'
import FileSaver from 'file-saver'
import { useGrpcRevalidateByMethod } from 'src/components/async/useGrpcMutate'

export const usePromptOverrideSources =
  (): GrpcResponse<ListPromptOverrideSourcesResponse> => {
    const request = new Empty()
    const { response } = useSwrImmutableGrpc(
      PromptOverrideSourcesPromiseClient.prototype.listPromptOverrideSources,
      request,
    )
    return NewGrpcResponse(response)
  }

export const useUploadPromptOverrideSource = (): (({
  name,
  file,
}: {
  name: string
  file: File
}) => Promise<string>) => {
  const client = useAuthorizedGrpcClient(PromptOverrideSourcesPromiseClient)
  return async ({ name, file }: { name: string; file: File }) => {
    const request = new CreatePromptOverrideSourceRequest()
    request.setName(name)
    request.setBody(new Uint8Array(await new Response(file).arrayBuffer()))
    const response = await client.createPromptOverrideSource(request)
    return response.getId()
  }
}

// delete prompt override source with confirmation modal
export const useDeletePromptOverrideSource = (): (({
  id,
}: {
  id: string
}) => Promise<void>) => {
  const { setConfirmationState } = useConfirmationContext()
  const client = useAuthorizedGrpcClient(PromptOverrideSourcesPromiseClient)
  const mutateFunc = useGrpcRevalidateByMethod()
  return async ({ id }: { id: string }) => {
    setConfirmationState({
      isShown: true,
      title: 'Delete Prompt Override Source',
      body: 'Are you sure you want to delete this prompt override source?',
      confirmText: 'Delete',
      onConfirmCB: async () => {
        const request = new DeletePromptOverrideSourceRequest()
        request.setId(id)
        await client.deletePromptOverrideSource(request)
        await mutateFunc(
          PromptOverrideSourcesPromiseClient.prototype
            .listPromptOverrideSources,
        )
        await mutateFunc(
          AccountSettingsPromiseClient.prototype.getAccountPromptOverrideSource,
        )
      },
    })
  }
}

export const useDownloadPromptOverrideSource = (): (({
  id,
}: {
  id: string
}) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(PromptOverrideSourcesPromiseClient)
  return async ({ id }: { id: string }) => {
    const request = new GetPromptOverrideSourceDownloadUrlRequest()
    request.setId(id)
    const response = await client.getPromptOverrideSourceDownloadUrl(request)
    const url = response.getUrl()
    const name = response.getName()
    const body = await fetch(url)
    const blob = await body.blob()
    FileSaver.saveAs(blob, name)
  }
}
